import { authFetch } from '../../services/apiServices';
import { SEVERITY_LEVELS, severityMeta, matchesQuery } from '../../config/warnings';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  TemplateToolbar, TemplateGrid, TemplateState, TemplateCard, TemplateText, TemplateAction, templateActionSx,
} from './TemplateShell';
import Toast                from '../actions/Toasts';
import ConfirmDialog        from '../common/ConfirmDialog';
import AddIcon              from '@mui/icons-material/Add';
import DeleteIcon           from '@mui/icons-material/DeleteOutline';
import EditIcon             from '@mui/icons-material/Edit';
import ReportProblemIcon    from '@mui/icons-material/ReportProblemOutlined';
import SaveIcon             from '@mui/icons-material/Save';

// ─── Shabllonet e Vërejtjeve Tab ─────────────────────────────────────────────
// Motra e `VendimetTab`, me dy fusha më shumë: NIVELI dhe SUBJEKTI. Te vendimet
// niveli nuk ekziston; te vërejtjet ai është vetë pesha e aktit — një shabllon
// "Shkelje e sigurisë" që nuk e mban nivelin do të regjistrohej si "Kujtesë",
// dhe dosja do të thoshte diçka tjetër nga ç'ndodhi.
export default function WarningTemplates() {
    const JSON_HDRS = { 'Content-Type': 'application/json' };
    const BLANK = { label: '', severity: 'Vërejtje', subject: '', comment: '' };

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving]       = useState(false);
    const [editItem, setEditItem]   = useState(null);
    const [addOpen, setAddOpen]     = useState(false);
    const [draft, setDraft]         = useState(BLANK);
    const [confirmDel, setConfirmDel] = useState(null);
    const [toast, setToast]         = useState({ open: false, message: '', variant: 'success' });
    const [search, setSearch]       = useState('');
    const [levelFilter, setLevelFilter] = useState('all');

    const say = (message, variant = 'success') => setToast({ open: true, message, variant });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await authFetch('/api/warning-templates').then(x => x.json());
            if (r?.success) { setTemplates(r.data || []); setLoadError(''); }
            else setLoadError(r?.error || 'Shabllonet nuk u ngarkuan.');
        } catch (_) {
            // Pa këtë degë, një server i rënë dukej si listë e zbrazët — dhe
            // përdoruesi do të kishte shtuar prej fillimi ato që i ka tashmë.
            setLoadError('Shabllonet nuk u ngarkuan. Kontrollo lidhjen me serverin.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const send = async (url, method, body) => {
        setSaving(true);
        try {
            const r = await authFetch(url, { method, headers: JSON_HDRS, body: JSON.stringify(body) }).then(x => x.json());
            if (r?.success) return true;
            say(r?.error || 'Veprimi nuk u krye.', 'error');
        } catch (_) { say('Veprimi nuk u krye.', 'error'); }
        finally { setSaving(false); }
        return false;
    };

    const handleAdd = async () => {
        if (!draft.label.trim()) return;
        if (await send('/api/warning-templates', 'POST', { ...draft, sort_order: templates.length })) {
            setDraft(BLANK); setAddOpen(false); load(); say('Shablloni u shtua.');
        }
    };

    const handleSaveEdit = async () => {
        if (!editItem?.label?.trim()) return;
        if (await send(`/api/warning-templates/${editItem.id}`, 'PUT', editItem)) {
            setEditItem(null); load(); say('Ndryshimet u ruajtën.');
        }
    };

    const handleDelete = async () => {
        const t = confirmDel;
        setSaving(true);
        try {
            const r = await authFetch(`/api/warning-templates/${t.id}`, { method: 'DELETE' }).then(x => x.json());
            if (r?.success) { setConfirmDel(null); load(); say('Shablloni u fshi.'); }
            else say(r?.error || 'Fshirja nuk u krye.', 'error');
        } catch (_) { say('Fshirja nuk u krye.', 'error'); }
        setSaving(false);
    };

    const toggleActive = async (tpl) => {
        // Fikja nuk e fshin: shablloni del nga lista e "Shto vërejtje" por
        // mbetet këtu, sepse vërejtjet e shkruara me të vazhdojnë të ekzistojnë.
        if (await send(`/api/warning-templates/${tpl.id}`, 'PUT', { ...tpl, active: !tpl.active })) load();
    };

    const filtered = templates.filter(t => {
        if (levelFilter !== 'all' && t.severity !== levelFilter) return false;
        return matchesQuery({ Subject: t.label, Description: `${t.subject || ''} ${t.comment || ''}` }, search);
    });

    return (
        <Box>
            <TemplateToolbar
                icon={<ReportProblemIcon />}
                title="Shabllonet e Vërejtjeve"
                hint="Shfaqen te «Shto vërejtje» dhe mbushin nivelin, subjektin e përshkrimin."
                search={search}
                onSearch={setSearch}
                action={
                    <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize:14 }} />}
                        onClick={() => { setDraft(BLANK); setAddOpen(true); }}
                        sx={templateActionSx}>
                        Shto shabllon
                    </Button>
                }
            >
                {/* Numërimi për nivel është njëherësh filtër. Ngjyra këtu është e
                    merituar — niveli i vërejtjes ËSHTË status, ndryshe nga lloji
                    i shabllonit, që tani e mban theksin e markës. */}
                <Stack direction="row" spacing={0.5} sx={{ flexWrap:'wrap' }}>
                    {SEVERITY_LEVELS.map((lvl) => {
                        const meta = severityMeta(lvl);
                        const n = templates.filter(t => t.severity === lvl).length;
                        const on = levelFilter === lvl;
                        return (
                            <Box key={lvl} component="button" type="button"
                                aria-pressed={on}
                                onClick={() => setLevelFilter(on ? 'all' : lvl)}
                                sx={{ cursor:'pointer', border:0, font:'inherit', textAlign:'left',
                                    px:1, py:0.25,
                                    borderLeft:'3px solid', borderColor: meta.color,
                                    bgcolor: on ? meta.bg : 'transparent', borderRadius:'0 4px 4px 0',
                                    '&:hover': { bgcolor: meta.soft },
                                    '&:focus-visible': { outline:'2px solid', outlineColor: meta.color, outlineOffset:-2 } }}>
                                <Typography sx={{ fontSize:'0.8rem', fontWeight:700, lineHeight:1.1, color: meta.color }}>{n}</Typography>
                                <Typography sx={{ fontSize:'0.6rem', color:'text.secondary', whiteSpace:'nowrap' }}>{lvl}</Typography>
                            </Box>
                        );
                    })}
                </Stack>
            </TemplateToolbar>

            <TemplateState
                loading={loading}
                error={loadError}
                empty={filtered.length === 0
                    ? (search.trim() || levelFilter !== 'all'
                        ? 'Asnjë shabllon nuk i përgjigjet kërkimit.'
                        : 'Asnjë shabllon. Shto të parin me "Shto shabllon".')
                    : ''}
            >
                <TemplateGrid>
                    {filtered.map((tpl) => {
                        const meta = severityMeta(tpl.severity);
                        const on = tpl.active !== false;
                        return (
                            <TemplateCard
                                key={tpl.id}
                                accent={meta.color}
                                dim={!on}
                                title={tpl.label}
                                titleAdornment={
                                    <Chip size="small" label={tpl.severity}
                                        sx={{ mt:0.4, height:17, fontSize:'0.61rem', fontWeight:700,
                                            color: meta.color, bgcolor: meta.bg, border:'1px solid', borderColor: meta.border }} />
                                }
                                aside={<Switch size="small" checked={on} onChange={() => toggleActive(tpl)} />}
                                actions={
                                    <>
                                        <TemplateAction label="Ndrysho" icon={<EditIcon sx={{ fontSize:14 }} />}
                                            onClick={() => setEditItem({ ...tpl })} />
                                        <TemplateAction label="Fshi" danger icon={<DeleteIcon sx={{ fontSize:14 }} />}
                                            onClick={() => setConfirmDel(tpl)} />
                                    </>
                                }
                            >
                                {tpl.subject && (
                                    <Typography sx={{ fontSize:'0.69rem', color:'text.secondary', fontWeight:600 }}>
                                        Subjekti: {tpl.subject}
                                    </Typography>
                                )}
                                <TemplateText lines={3} muted={!tpl.comment}>
                                    {tpl.comment || 'Pa përshkrim'}
                                </TemplateText>
                            </TemplateCard>
                        );
                    })}
                </TemplateGrid>
            </TemplateState>

            <WarningTemplateDialog
                open={addOpen} title="Shto shabllon vërejtjeje" value={draft} saving={saving}
                onChange={setDraft} onClose={() => setAddOpen(false)} onSave={handleAdd}
                saveLabel="Shto" saveIcon={<AddIcon />}
            />
            <WarningTemplateDialog
                open={Boolean(editItem)} title="Ndrysho shabllonin" value={editItem || BLANK} saving={saving}
                onChange={(v) => setEditItem(v)} onClose={() => setEditItem(null)} onSave={handleSaveEdit}
                saveLabel="Ruaj" saveIcon={<SaveIcon />}
            />

            <ConfirmDialog
                open={Boolean(confirmDel)}
                title="Fshi shabllonin"
                message="Vërejtjet e shkruara më parë me këtë shabllon nuk preken — teksti i tyre është ruajtur bashkë me vërejtjen."
                details={confirmDel ? `${confirmDel.label} · ${confirmDel.severity}` : ''}
                confirmLabel="Fshi"
                busy={saving}
                onConfirm={handleDelete}
                onClose={() => setConfirmDel(null)}
            />

            <Toast open={toast.open} message={toast.message} variant={toast.variant}
                onClose={() => setToast(t => ({ ...t, open: false }))} />
        </Box>
    );
}

function WarningTemplateDialog({ open, title, value, saving, onChange, onClose, onSave, saveLabel, saveIcon }) {
    const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ pt:'12px !important' }}>
                <Stack spacing={2}>
                    <TextField label="Etiketa (emri i shabllonit)" value={value.label || ''} onChange={set('label')}
                        fullWidth size="small" autoFocus placeholder="p.sh. Mungesë pa arsyetim" />
                    <Stack direction="row" spacing={2}>
                        <TextField select label="Niveli" value={value.severity || 'Vërejtje'} onChange={set('severity')}
                            size="small" sx={{ width:200 }}>
                            {SEVERITY_LEVELS.map(l => (
                                <MenuItem key={l} value={l}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor: severityMeta(l).color }} />
                                        <span>{l}</span>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField label="Subjekti" value={value.subject || ''} onChange={set('subject')}
                            fullWidth size="small" placeholder="p.sh. Mungesë e paarsyetuar" />
                    </Stack>
                    <TextField label="Përshkrimi (teksti që mbushet automatikisht)" value={value.comment || ''}
                        onChange={set('comment')} fullWidth size="small" multiline minRows={5}
                        placeholder="Kllapat katrore mbeten për t'u plotësuar me dorë: [Emri Mbiemri], [Data]…" />
                    <Typography variant="caption" color="text.secondary">
                        Vendos në tekst [Emri Mbiemri], [Data] ose [Përshkrimi i rastit] — mbeten të dukshme që të
                        plotësohen para dërgimit.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Anulo</Button>
                <Button onClick={onSave} variant="contained" disabled={saving || !value.label?.trim()}
                    startIcon={saving ? <CircularProgress size={13} color="inherit" /> : saveIcon}>
                    {saving ? 'Duke ruajtur…' : saveLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
